import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { Card, Button, Field, Summary } from '@gonka-wallet/ui-tokens';
import { formatExplorerUrl, WalletError } from '@gonka-wallet/chain';
import { validateGonkaAddress } from '@gonka-wallet/crypto';
import { useWalletStore, useActiveEntry } from '../stores/walletStore.js';
import { useBalance } from '../hooks/useBalance.js';
import { useSignAndBroadcast } from '../hooks/useSignAndBroadcast.js';
import { PinPad } from '../components/PinPad.js';
import { DotProgress } from '../components/DotProgress.js';
import { BackLink } from '../components/BackLink.js';
import { formatGnk, parseGnkToNgonka } from '../lib/format.js';

type Stage = 'form' | 'review' | 'pin' | 'result';

export function Send() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeEntry = useActiveEntry();
  const wallet = useWalletStore((s) =>
    s.wallets.find((w) => w.id === s.activeId),
  );
  const { data: balance } = useBalance(wallet?.addresses[0] ?? null);
  const mutation = useSignAndBroadcast();

  const [to, setTo] = useState('');
  const [amountGnk, setAmountGnk] = useState('');
  const [memo, setMemo] = useState('');
  const [stage, setStage] = useState<Stage>('form');
  const [txHash, setTxHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [toFocused, setToFocused] = useState(false);
  const [amountFocused, setAmountFocused] = useState(false);
  const [memoFocused, setMemoFocused] = useState(false);

  const trimmedTo = to.trim();
  const amountNgonka = (() => {
    try {
      return amountGnk ? parseGnkToNgonka(amountGnk) : '0';
    } catch {
      return '0';
    }
  })();
  const available = balance?.available ?? '0';
  const addressValid = trimmedTo.length > 0 && validateGonkaAddress(trimmedTo);
  const amountValid = amountNgonka !== '0';
  const amountFits =
    amountValid && BigInt(amountNgonka) <= BigInt(available);
  const canSubmit = addressValid && amountValid && amountFits;

  // Show inline hints only after the user has typed something — don't
  // accuse them of an error before they've started.
  const addressHint =
    trimmedTo.length > 0 && !addressValid
      ? t('errors.tx.invalidRecipient')
      : null;
  const amountHint =
    amountGnk.trim().length > 0 && !amountValid
      ? t('send.amountInvalid')
      : amountValid && !amountFits
        ? t('errors.tx.insufficientBalance')
        : null;

  async function sendWithPin(pin: string) {
    if (!wallet) return;
    setError(null);
    try {
      const hash = await mutation.mutateAsync({
        wallet,
        pin,
        to: trimmedTo,
        amountNgonka,
        memo: memo || undefined,
      });
      setTxHash(hash);
      setStage('result');
    } catch (err) {
      const msg =
        err instanceof WalletError ? t(err.i18nKey) : (err as Error).message;
      setError(msg);
      setStage('review');
    }
  }

  if (activeEntry?.kind === 'watch') {
    return <WatchOnlyBanner t={t} navigate={navigate} />;
  }

  if (stage === 'result' && txHash) {
    const url = formatExplorerUrl(txHash);
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-bg p-4">
        <SuccessCheck />
        <h1 className="text-[22px] font-semibold tracking-[-0.5px] text-text-primary">
          {t('send.success')}
        </h1>
        <code className="break-all rounded-lg bg-surface-1 px-3 py-2 font-mono text-[11px] text-text-secondary">
          {txHash}
        </code>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] font-medium text-link hover:opacity-80"
          >
            {t('send.viewExplorer')} ↗
          </a>
        )}
        <Button variant="primary" onClick={() => navigate('/wallet')} fullWidth>
          OK
        </Button>
      </div>
    );
  }

  if (stage === 'pin') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6">
        {mutation.isPending ? (
          <div className="flex flex-col items-center gap-4">
            <DotProgress size={10} gap={8} />
            <p className="text-[13px] font-medium text-text-primary">
              {t('send.sending')}
            </p>
            <p className="text-[11px] text-text-tertiary">
              {t('send.broadcasting')}
            </p>
          </div>
        ) : (
          <PinPad onComplete={sendWithPin} disabled={mutation.isPending} />
        )}
        {error && <p className="mt-4 text-neg">{error}</p>}
      </div>
    );
  }

  if (stage === 'review') {
    return (
      <div className="flex min-h-screen flex-col bg-bg p-4">
        <div className="flex items-center pb-2 pt-1">
          <button
            type="button"
            onClick={() => setStage('form')}
            className="text-sm font-medium text-link"
          >
            ‹ {t('common.back')}
          </button>
        </div>

        <div className="mt-2 text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
          {t('send.review')}
        </div>

        <div className="mt-3 rounded-xl bg-surface-1 px-4 py-3.5">
          <div className="mb-1 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
            {t('send.recipient')}
          </div>
          <div className="break-all font-mono text-[13px] text-text-primary">{to}</div>
        </div>

        <Summary
          rows={[
            {
              key: t('send.amount'),
              value: <span className="font-mono tabular-nums">{amountGnk} GNK</span>,
            },
            {
              key: t('send.fee'),
              value: <span className="font-mono">{t('send.feeZero')}</span>,
            },
          ]}
        />

        {error && <p className="mt-2 text-[11px] text-neg">{error}</p>}

        <div className="sticky inset-x-0 bottom-0 -mx-4 mt-auto flex flex-col gap-2 bg-bg px-4 pb-[max(env(safe-area-inset-bottom,0px),16px)] pt-4">
          <Button variant="primary" onClick={() => setStage('pin')} fullWidth>
            {t('send.confirm')}
          </Button>
          <Button variant="ghost" onClick={() => setStage('form')} fullWidth>
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col gap-3 p-4">
      <BackLink to="/wallet" label={t('common.back')} />
      <h1 className="text-2xl font-semibold">{t('send.title')}</h1>
      <Field
        label={t('send.recipient')}
        hint={
          addressHint ? (
            <span className="text-neg">{addressHint}</span>
          ) : addressValid ? (
            <span className="text-pos">&#10003; Valid address</span>
          ) : undefined
        }
        focused={toFocused}
      >
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          onFocus={() => setToFocused(true)}
          onBlur={() => setToFocused(false)}
          placeholder={t('send.recipient')}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className="w-full bg-transparent outline-none font-mono text-sm text-text-primary"
        />
      </Field>
      <Field
        label="Amount"
        hint={
          amountHint ? (
            <span className="text-neg">{amountHint}</span>
          ) : balance ? (
            <span className="text-text-tertiary">
              {t('send.available', { amount: formatGnk(balance.available) })}
            </span>
          ) : undefined
        }
        focused={amountFocused}
      >
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="decimal"
            value={amountGnk}
            onChange={(e) => setAmountGnk(e.target.value)}
            onFocus={() => setAmountFocused(true)}
            onBlur={() => setAmountFocused(false)}
            placeholder="0.0"
            className="w-full bg-transparent outline-none font-mono text-2xl tabular-nums text-text-primary"
          />
          <button
            type="button"
            onClick={() => balance && setAmountGnk(formatGnk(balance.available))}
            className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.5px] text-link"
          >
            {t('send.max')}
          </button>
        </div>
      </Field>
      <Field
        label={t('send.memo')}
        focused={memoFocused}
      >
        <input
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          onFocus={() => setMemoFocused(true)}
          onBlur={() => setMemoFocused(false)}
          placeholder={t('send.memo')}
          className="w-full bg-transparent outline-none text-text-primary"
        />
      </Field>
      <Button
        variant="primary"
        disabled={!canSubmit}
        onClick={() => setStage('review')}
        fullWidth
      >
        {t('send.review')}
      </Button>
    </div>
  );
}

function SuccessCheck(): ReactNode {
  return (
    <div className="relative flex h-[104px] w-[104px] items-center justify-center">
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: 'color-mix(in oklab, var(--color-pos) 12%, transparent)',
          animation: 'gk-check-ring 480ms cubic-bezier(0.2,0.85,0.35,1.05) both',
        }}
      />
      <span
        aria-hidden
        className="relative flex h-[72px] w-[72px] items-center justify-center rounded-full"
        style={{ background: 'var(--color-pos)' }}
      >
        <svg
          width="38"
          height="38"
          viewBox="0 0 38 38"
          fill="none"
          aria-hidden
        >
          <path
            d="M10 20 L17 27 L29 13"
            stroke="white"
            strokeWidth={3.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 40,
              strokeDashoffset: 40,
              animation:
                'gk-check-draw 420ms cubic-bezier(0.2,0.85,0.35,1.05) 120ms both',
            }}
          />
        </svg>
      </span>
      <style>{`
        @keyframes gk-check-ring {
          0%   { transform: scale(0.6); opacity: 0; }
          100% { transform: scale(1.0); opacity: 1; }
        }
        @keyframes gk-check-draw {
          0%   { stroke-dashoffset: 40; }
          100% { stroke-dashoffset: 0;  }
        }
      `}</style>
    </div>
  );
}

function WatchOnlyBanner({
  t,
  navigate,
}: {
  readonly t: (key: string) => string;
  readonly navigate: (path: string) => void;
}): ReactNode {
  return (
    <div className="flex min-h-screen flex-col gap-4 p-4">
      <h1 className="text-2xl font-semibold">{t('send.title')}</h1>
      <Card className="flex flex-col items-center gap-3 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-link/10 text-link">
          <Eye className="h-6 w-6" />
        </div>
        <div className="text-base font-semibold text-text-primary">
          {t('send.watchOnlyBlocked.title')}
        </div>
        <div className="max-w-xs text-sm text-text-secondary">
          {t('send.watchOnlyBlocked.body')}
        </div>
      </Card>
      <Button
        variant="primary"
        onClick={() => navigate('/import')}
        fullWidth
      >
        {t('send.watchOnlyBlocked.importCta')}
      </Button>
      <Button variant="ghost" onClick={() => navigate('/wallet')} fullWidth>
        {t('common.back')}
      </Button>
    </div>
  );
}

